import {
  Model,
  Attr,
  HasOne,
  HasMany,
  PersistedSpraypaintRecord,
} from 'spraypaint';
import { LayoutItem, FulfilledLayout } from 'spa-ionata-admin/types';
import ApplicationRecord from 'spa-ionata-admin/lib/models/ApplicationRecord';
import {
  sanitizedArrangement,
  getLayoutSlots,
  layoutsMap,
} from 'spa-ionata-admin/lib/services/Layouts';
import LocaleItem from '../services/LocaleItem';
import User from './User';
import Category from './Category';
import Media, { PersistedMedia } from './Media';
import Award from './Award';
import ExportMarket from './ExportMarket';

// NOTE: feel free to comment-out this const and use the [string, Media] type
const imageKeys = [
  'logo',
  'coverImage',
  'asideImage',
  'quoteImage',
  'images',
] as const;

export type ImagesKey = typeof imageKeys[number];

// export type KeyedMedia = [string, Media];
export type KeyedMedia = [ImagesKey, Media];

// export type KeyedPersistedMedia = [string, PersistedMedia];
export type KeyedPersistedMedia = [ImagesKey, PersistedMedia];

export type PersistedBaseBusiness = PersistedSpraypaintRecord<BaseBusiness>;

@Model()
export default class BaseBusiness extends ApplicationRecord {
  static jsonapiType = '__invalid-jsonapi-type-base-businesses__';

  // --- Attributes
  // --------------

  // required
  @Attr() name!: TranslatableText;

  @Attr() excerpt!: PartialTranslatableText | null;

  @Attr() intro!: PartialTranslatableText | null;

  @Attr() description!: PartialTranslatableText | null;

  @Attr() quoteOverline!: PartialTranslatableText | null;

  @Attr() quoteText!: PartialTranslatableText | null;

  @Attr() quotePerson!: PartialTranslatableText | null;

  @Attr() quoteJob!: PartialTranslatableText | null;

  @Attr() kspText!: string | null; // NOTE: Yup - not localized :)

  @Attr() facebook!: string | null;

  @Attr() twitter!: string | null;

  @Attr() instagram!: string | null;

  @Attr() linkedIn!: string | null;

  @Attr() website!: string | null;

  @Attr() phoneNumber!: string | null;

  // maxLength: 254
  @Attr() email!: string | null;

  @Attr() isLogoOverImage!: boolean;

  @Attr() excerptAsSubtitle!: boolean;

  @Attr() metadata!: HashAny | null;

  // readOnly
  @Attr() createdAt!: string | null;

  // readOnly
  @Attr() modifiedAt!: string | null;

  // --- Relationships
  // -----------------

  @HasMany() categories!: Category[];

  @HasMany() images!: Media[];

  @HasMany() awards!: Award[]; // NOTE: up to three

  @HasMany() exportMarkets!: ExportMarket[];

  // nullable
  @HasOne() logo!: Media | null;

  // nullable
  @HasOne() coverImage!: Media | null;

  // nullable
  @HasOne() asideImage!: Media | null;

  // nullable
  @HasOne() quoteImage!: Media | null;

  // nullable
  @HasOne() createdBy!: User | null;

  // nullable
  @HasOne() modifiedBy!: User | null;

  // --- Custom
  // ----------

  readonly singleMediaKeys = [
    'logo',
    'coverImage',
    'asideImage',
    'quoteImage',
    //
  ];

  get mainCategory() {
    return this.categories[0];
  }

  get quoteImageProcessUrl() {
    return this.id ? this.klass.url(this.id, 'quote-image/') : undefined;
  }

  get logoProcessUrl() {
    return this.id ? this.klass.url(this.id, 'logo/') : undefined;
  }

  get coverImageProcessUrl() {
    return this.id ? this.klass.url(this.id, 'cover-image/') : undefined;
  }

  get asideImageProcessUrl() {
    return this.id ? this.klass.url(this.id, 'aside-image/') : undefined;
  }

  get imagesProcessUrl() {
    return this.id ? this.klass.url(this.id, 'images/') : undefined;
  }

  constructor(attrs?: HashAny) {
    super(attrs);

    this.locale = new LocaleItem(this, {
      keys: [
        'name',
        'excerpt',
        'intro',
        'description',
        'quoteOverline',
        'quoteText',
        'quotePerson',
        'quoteJob',
      ],
    }).asHash();
    // CHECK: Is locale creating a strong reference to this? Do we need to free it?
  }

  locale: Hash<string | undefined>;

  mergedMetadata(data: HashAny): HashAny | null {
    const newMetadata: HashAny = {
      ...this.metadata,
      ...data,
    };
    if (Object.keys(newMetadata).length) {
      return newMetadata;
    }
    return null;
  }

  mergeMetadata(data: HashAny) {
    this.metadata = this.mergedMetadata(data);
  }

  get quoteAspectRatio() {
    const { quoteAspectRatio } = this.metadata || {};
    if (!quoteAspectRatio) {
      return undefined;
    }
    const value = parseFloat(quoteAspectRatio);
    return value && value >= 0.5 && value <= 2 ? value : undefined;
  }

  getGalleryLayouts(): LayoutItem[] | undefined {
    const { galleryLayouts } = this.metadata || {};
    if (galleryLayouts) {
      const arrangement = sanitizedArrangement(galleryLayouts);
      if (arrangement?.length) {
        return arrangement;
      }
    }
    return undefined;
  }

  getDefaultLayouts() {
    const count = this.images?.length || 0;
    if (count) {
      const layouts = this.getGalleryLayouts();
      if (layouts) {
        return layouts;
      }
      if (count > 2) {
        return [layoutsMap.tripleTrailing];
      }
      if (count > 1) {
        return [layoutsMap.doubleTrailing];
      }
      return [layoutsMap.single];
    }
    return undefined;
  }

  getGalleryMediaIds({ fallback = true } = {}): FulfilledLayout[] | undefined {
    const { images } = this;
    if (!images.length) {
      return undefined;
    }
    const layouts =
      this.getGalleryLayouts() ||
      (fallback ? this.getDefaultLayouts() : undefined);
    if (!layouts) {
      return undefined;
    }
    const slots = getLayoutSlots(layouts);
    if (!slots) {
      return undefined;
    }
    const mediaIds = images.map(media => media.id || '').filter(id => id);
    let slottedMediaIds: FulfilledLayout[] = slots.map((col, colIdx) => {
      const { key, layout } = layouts[colIdx];
      return {
        key,
        layout,
        ids: col.map(index => mediaIds[index]),
      };
    });
    const cutoffIndex = slottedMediaIds.findIndex(col =>
      col.ids.some(id => !id),
    );
    if (cutoffIndex !== -1) {
      slottedMediaIds = slottedMediaIds.slice(0, cutoffIndex);
    }
    if (slottedMediaIds.length) {
      return slottedMediaIds;
    }
    return undefined;
  }

  getAllMediasWithKeys(): KeyedMedia[] {
    return [
      ['logo', this.logo],
      ['coverImage', this.coverImage],
      ['asideImage', this.asideImage],
      ['quoteImage', this.quoteImage],
      ...this.images.map(media => ['images', media]),
    ].filter(([, media]) => media) as KeyedMedia[];
  }

  getAllPersistedMediasWithKeys(): KeyedPersistedMedia[] {
    return this.getAllMediasWithKeys().filter(
      ([, media]) => media.id,
    ) as KeyedPersistedMedia[];
  }

  getAllPersistedMedias(): PersistedMedia[] {
    return this.getAllPersistedMediasWithKeys().map(([, media]) => media);
  }

  getAllMediaIds(): string[] {
    return this.getAllPersistedMedias().map(media => media.id);
  }

  async fetchAllMediasWithUUIDs({ onlyUUIDs = true } = {}): Promise<
    PersistedMedia[]
  > {
    const noMedia = (id: string | undefined) => {
      throw new Error(`no media for id: ${id}`);
    };
    return Promise.all(
      this.getAllPersistedMedias().map(
        (media: PersistedMedia): Promise<PersistedMedia> => {
          if (media.uuid) {
            return Promise.resolve(media);
          }
          const scope = onlyUUIDs ? Media.select(['uuid']) : Media.includes([]);
          return scope
            .find(media.id)
            .then(res => res.data || noMedia(media.id));
          // CHECK: should we rather _ingore_ when there is no media found?
        },
      ),
    );
  }

  getImagesUUIDs(uuidsMap?: Hash<PersistedMedia>) {
    this.images.forEach(media => {
      if (!media.uuid) {
        throw new Error('got a media with no uuid');
      }
    });
    return Media.getMediasUUIDs(this.images as PersistedMedia[], uuidsMap);
  }

  // --- merging

  // Merges all data from another Business and saves
  //
  // It is meant to be used to merge data from Business into a DraftBusiness and
  // when publishing - from DraftBusiness to a Business.
  //
  // The result is an array of non-fatal errors.
  async mergeAndSaveAllDataFromSource(source: BaseBusiness): Promise<string[]> {
    // keep track of non-fatal errors
    const errors: string[] = [];
    // capture the images before fetching the medias
    const sourceImages = source.images.slice();
    const ourImages = this.images.slice();
    // get the medias
    const [sourceMedias, ourMedias] = await Promise.all([
      source.fetchAllMediasWithUUIDs(),
      this.fetchAllMediasWithUUIDs(),
    ]);
    const allMedias = sourceMedias.concat(ourMedias);
    // console.log('allMedias:', allMedias);
    // make UUIDs/IDs lookup tables
    const sourceMediasMap = Media.getPersistedMediasLookupTable(sourceMedias);
    const ourMediasMap = Media.getPersistedMediasLookupTable(ourMedias);
    // work out if we'll need to update the this.images - re-order, remove or add
    const sourceImagesUUIDs = sourceImages.map<string>(
      media => sourceMediasMap[media.asPersisted().id]?.uuid,
    );
    // console.log('sourceImagesUUIDs:', sourceImagesUUIDs);
    const ourImagesUUIDs = ourImages.map<string>(
      media => ourMediasMap[media.asPersisted().id]?.uuid,
    );
    // console.log('ourImagesUUIDs:', ourImagesUUIDs);
    const needsImagesUpdate =
      sourceImagesUUIDs.join(',') !== ourImagesUUIDs.join(',');
    // console.log('needsImagesUpdate:', needsImagesUpdate);
    // merge all the relevant attributes and relationships
    this.mergeDataWithMediasFromSource(source, allMedias);
    // this.mergeBusiness(source, allMedias);
    const ourHash = this as HashAny;
    // determine which medias need to be cloned
    const toClone = this.getAllMediasWithKeys()
      .filter(([, media]) => media.cloneFrom)
      .map(([key, media]) => [
        key,
        media,
        ourHash[`${key as string}ProcessUrl`] || '',
      ]) as [string, Media, string][];
    // console.log('toClone:', toClone);
    // check and remove any images that will be cloned
    const toDestroy = allMedias.filter(media => media.isMarkedForDestruction);
    // console.log('toDestroy:', toDestroy);
    if (toDestroy.length) {
      await Promise.all(Media.destroyMarkedItems(toDestroy));
    }
    // keep a list of images to insert
    const clonedImagesMap: Hash<PersistedMedia> = {};
    if (toClone.length) {
      const promises = toClone.map(async ([key, media, url]) => {
        const result: [string, Media | null] = [key, null];
        if (!url) {
          errors.push(`no process url for ${key}`);
          return result;
        }
        // actually clone the media
        const clonedMedia = await media.clone(url);
        if (key === 'images') {
          clonedImagesMap[clonedMedia.uuid] = clonedMedia;
        } else {
          // console.log('assigning:', key, '=', clonedMedia);
          ourHash[key] = clonedMedia;
        }
        result[1] = clonedMedia;
        return result;
      });
      // const done = await Promise.all(promises);
      // console.log('cloned:', done);
      // CHECK: should we review if any medias are null?
      await Promise.all(promises);
    }
    // console.log('clonedImagesMap:', clonedImagesMap);
    if (needsImagesUpdate) {
      // fix order of images
      const missingMedia = (uuid: string) => {
        errors.push(`no media for uuid: ${uuid}`);
      };
      this.images = sourceImagesUUIDs.map(
        uuid =>
          clonedImagesMap[uuid] || ourMediasMap[uuid] || missingMedia(uuid),
      );
    }
    // console.log('this:', this);
    // console.log(
    //   'json:',
    //   this.asJSON([
    //     'categories.id',
    //     'logo.id',
    //     'coverImage.id',
    //     'asideImage.id',
    //     'quoteImage.id',
    //     'images.id',
    //   ]),
    // );
    const ok = await this.save({ with: ['categories.id', 'images.id'] });
    if (!ok) {
      errors.push('a non-fatal error occurred while saving business');
    }
    return errors;
  }

  mergeDataWithMediasFromSource<I extends BaseBusiness>(
    source: I,
    mediasWithUUIDs: PersistedMedia[] = [],
  ) {
    mediasWithUUIDs.forEach(media => {
      if (!media.uuid) {
        throw new Error('got a media with no uuid');
      }
    });
    const fromHash = source as HashAny;
    const toHash = this as HashAny;
    const idMap: Hash<PersistedMedia> = {};
    mediasWithUUIDs.forEach(media => {
      idMap[media.id] = media;
    });
    const noId = () => {
      // console.warn('no id');
      throw new Error('media with no id');
    };
    const uuidMap: Hash<PersistedMedia> = {};
    this.images.forEach(m => {
      const media = idMap[m.id || noId()];
      uuidMap[media.uuid] = media;
    });
    const getMedia = (
      theirId: string | undefined,
      ourId: string | undefined,
    ): Media | null => {
      const theirs: PersistedMedia | undefined = idMap[theirId || ''];
      const ours: PersistedMedia | undefined = idMap[ourId || ''];
      if (!theirs && !ours) {
        return null;
      }
      if (!theirs && ours) {
        // have to remove ours
        ours.isMarkedForDestruction = true;
        return ours;
      }
      if ((theirs && !ours) || theirs.uuid !== ours.uuid) {
        // have to clone theirs (and remove ours)
        if (ours) {
          ours.isMarkedForDestruction = true;
        }
        return new Media({ cloneFrom: theirs });
      }
      // can keep ours
      return ours;
    };
    this.singleMediaKeys.forEach(key => {
      toHash[key] = getMedia(fromHash[key]?.id, toHash[key]?.id);
    });
    this.images = (source.images || []).map(m => {
      const theirs = idMap[m.id || noId()];
      const ours = uuidMap[theirs.uuid];
      if (ours) {
        // console.log('got ours for:', theirs.id, 'uuid:', theirs.uuid);
        delete uuidMap[theirs.uuid];
      } else {
        // console.log('no ours for:', theirs.id, 'uuid:', theirs.uuid);
      }
      // reuse ours or clone theirs
      return ours ?? new Media({ cloneFrom: theirs });
    });
    // mark lefover images for removal
    Object.keys(uuidMap).map(key => {
      const toDestroy = uuidMap[key];
      toDestroy.isMarkedForDestruction = true;
    });
    // merge all data except specific keys defined via getMergeKeysToIgnore
    this.mergeDataFrom(source, ['images', ...this.singleMediaKeys]);
  }

  mergeDataFrom<I extends ApplicationRecord>(
    source: I,
    ignoredKeys: string[] = [],
  ) {
    super.mergeDataFrom(source, this.getMergeKeysToIgnore(ignoredKeys));
  }

  getMergeKeysToIgnore(keys: string[]) {
    // we ensure to ignore the singleMediaKeys and images
    return [...keys, 'createdAt', 'modifiedAt'];
  }
}
